import React, { useEffect, useRef, useState } from 'react'
import { Box, Container, Link, Stack, Typography } from '@mui/material'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'

import type { HolidayImage } from '@/src/types/holidays'
import { HOLIDAYS_BANNER_ASPECT_RATIO, HOLIDAYS_MAX_WIDTH } from '@/src/utils/holidays'

import HolidayImageView from '../holidayImage'
import PoweredByWrapper from '../poweredBy'

/** Lines of intro shown before "View more". */
const INTRO_COLLAPSED_LINES = 2

const INTRO_STYLE = {
    subheading: { mobileSize: '0.875rem', desktopSize: '1.125rem', lineHeight: 1.5 },
    paragraph: { mobileSize: '0.875rem', desktopSize: '0.938rem', lineHeight: 1.75 }
} as const

interface CollectionHeroProps {
    /** Rendered directly above the heading, inside the page's container. */
    breadcrumbs?: React.ReactNode
    heading: string
    subheading?: string
    banner?: HolidayImage
    paragraphs: string[]
    showMoreLabel: string
    showLessLabel: string
    phoneLabel?: string
    emailAddress?: string
    emailHref?: string
    isMobile: boolean
}

const CollectionHero: React.FC<CollectionHeroProps> = ({
    breadcrumbs,
    heading,
    subheading,
    banner,
    paragraphs,
    showMoreLabel,
    showLessLabel,
    phoneLabel,
    emailAddress,
    emailHref,
    isMobile
}) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const [fitsInline, setFitsInline] = useState(true)
    const measureRef = useRef<HTMLParagraphElement>(null)

    // Subheading and paragraphs read as one intro: collapsed, only its first two lines show.
    const introBlocks = [
        ...(subheading ? [{ text: subheading, isSubheading: true }] : []),
        ...paragraphs.map(text => ({ text, isSubheading: false }))
    ]
    const firstBlock = introBlocks[0]

    // A hidden, unclamped copy of the first block plus the link says whether both fit in two lines.
    // Re-runs on collapse: the copy remounts then, and the resize listener must track the new node.
    useEffect(() => {
        const element = measureRef.current
        if (!element || isExpanded) return undefined

        const measure = () => {
            const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight)
            setFitsInline(element.scrollHeight <= lineHeight * INTRO_COLLAPSED_LINES + 1)
        }
        measure()
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [isExpanded, firstBlock?.text, showMoreLabel])

    const hasMore = introBlocks.length > 1 || !fitsInline

    const blockSx = (isSubheading: boolean) => {
        const style = isSubheading ? INTRO_STYLE.subheading : INTRO_STYLE.paragraph
        return {
            fontSize: isMobile ? style.mobileSize : style.desktopSize,
            lineHeight: style.lineHeight,
            color: 'textColors.secondary'
        }
    }

    const toggleLink = (
        <Link
            component="button"
            type="button"
            underline="none"
            onClick={() => setIsExpanded(current => !current)}
            // `font: inherit` so the button reads exactly like the sentence it ends; only the colour differs.
            sx={{ font: 'inherit', color: 'primary.main', verticalAlign: 'baseline' }}
        >
            {isExpanded ? showLessLabel : showMoreLabel}
        </Link>
    )

    return (
        <Box component="header">
            {banner && (
                <Box
                    sx={{
                        aspectRatio: isMobile ? HOLIDAYS_BANNER_ASPECT_RATIO.MOBILE : HOLIDAYS_BANNER_ASPECT_RATIO.DESKTOP,
                        bgcolor: 'neutral.100',
                        position: 'relative'
                    }}
                >
                    <HolidayImageView image={banner} lazy={false} isMobile={isMobile} />
                </Box>
            )}

            <Container maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH, py: isMobile ? 2.5 : 4 }}>
                <Stack spacing={isMobile ? 1.5 : 2}>
                    {breadcrumbs}

                    <Typography
                        component="h1"
                        sx={{
                            fontSize: isMobile ? '1.5rem' : '2.25rem',
                            fontWeight: 700,
                            lineHeight: 1.2,
                            color: 'textColors.primary'
                        }}
                    >
                        {heading}
                    </Typography>

                    {firstBlock && !isExpanded && (
                        <Box sx={{ position: 'relative' }}>
                            <Typography
                                ref={measureRef}
                                aria-hidden
                                sx={{
                                    ...blockSx(firstBlock.isSubheading),
                                    position: 'absolute',
                                    insetInlineStart: 0,
                                    insetInlineEnd: 0,
                                    visibility: 'hidden',
                                    pointerEvents: 'none'
                                }}
                            >
                                {firstBlock.text} <Box component="span">{showMoreLabel}</Box>
                            </Typography>

                            {fitsInline ? (
                                <Typography sx={blockSx(firstBlock.isSubheading)}>
                                    {firstBlock.text}
                                    {hasMore && <> {toggleLink}</>}
                                </Typography>
                            ) : (
                                <Typography
                                    sx={{
                                        ...blockSx(firstBlock.isSubheading),
                                        position: 'relative',
                                        display: '-webkit-box',
                                        WebkitLineClamp: INTRO_COLLAPSED_LINES,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {firstBlock.text}
                                    {/* Too long for the link to follow it: it caps the second line instead. */}
                                    <Box
                                        component="span"
                                        sx={{
                                            position: 'absolute',
                                            insetInlineEnd: 0,
                                            bottom: 0,
                                            paddingInlineStart: 0.5,
                                            bgcolor: 'background.default'
                                        }}
                                    >
                                        … {toggleLink}
                                    </Box>
                                </Typography>
                            )}
                        </Box>
                    )}

                    {isExpanded && (
                        <Stack spacing={1}>
                            {introBlocks.map((block, index) => (
                                <Typography key={block.text} sx={blockSx(block.isSubheading)}>
                                    {block.text}
                                    {index === introBlocks.length - 1 && <> {toggleLink}</>}
                                </Typography>
                            ))}
                        </Stack>
                    )}

                    {(phoneLabel || emailHref) && (
                        <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? 0.75 : 3}>
                            {phoneLabel && (
                                <Box
                                    component="span"
                                    dir="ltr"
                                    sx={{ color: 'primary.main', fontSize: '0.875rem', fontWeight: 600 }}
                                >
                                    {phoneLabel}
                                </Box>
                            )}
                            {emailHref && (
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    component="a"
                                    href={emailHref}
                                    sx={{
                                        alignItems: 'center',
                                        textDecoration: 'none',
                                        color: 'primary.main',
                                        fontSize: '0.875rem',
                                        fontWeight: 600
                                    }}
                                >
                                    <MailOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                    <Box component="span" dir="ltr">
                                        {emailAddress}
                                    </Box>
                                </Stack>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PoweredByWrapper height={18} />
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </Container>
        </Box>
    )
}

export { CollectionHero }
export type { CollectionHeroProps }
export default CollectionHero
